import React, { useState, useEffect, useCallback } from 'react';
import { legalApi } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';

const PolicyConsentCheckbox = ({ accepted, onChange, hasError }) => {
  const [policies, setPolicies] = useState([]);
  const [viewingPolicy, setViewingPolicy] = useState(null);

  const fetchPolicies = useCallback(async () => {
    try {
      const data = await legalApi.getRequiredPolicies();
      setPolicies(data.policies || []);
    } catch {}
  }, []);

  useEffect(() => { fetchPolicies(); }, [fetchPolicies]);

  const getPoliciesData = () => policies.map(p => ({
    page_id: p.page_id,
    slug: p.slug,
    version: p.version || 1,
  }));

  const handleChange = (checked) => {
    onChange(checked, checked ? getPoliciesData() : []);
  };

  return (
    <>
      <div className={`rounded-lg transition-colors ${hasError ? 'p-2.5 bg-red-500/10 border border-red-500/30' : ''}`}>
        <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid="policy-consent-checkbox">
          <input
            type="checkbox"
            checked={accepted}
            onChange={e => handleChange(e.target.checked)}
            className="accent-[#8B5CF6] mt-1 w-4 h-4 flex-shrink-0"
            data-testid="policy-checkbox"
          />
          <span className="text-sm text-gray-400 leading-snug">
            I agree to the{' '}
            {policies.map((p, i) => (
              <span key={p.page_id}>
                {i > 0 && i < policies.length - 1 && ', '}
                {i > 0 && i === policies.length - 1 && ' and '}
                <button type="button" onClick={(e) => { e.preventDefault(); setViewingPolicy(p); }}
                  className="text-[#8B5CF6] hover:text-[#A78BFA] underline" data-testid={`view-policy-${p.slug}`}>
                  {p.title}
                </button>
              </span>
            ))}
            {policies.length === 0 && <span className="text-[#8B5CF6]">Terms and Conditions and Privacy Policy</span>}
          </span>
        </label>
        {hasError && (
          <p className="text-xs text-red-400 mt-1.5 ml-6" data-testid="policy-error-message">
            You must accept all policies to continue
          </p>
        )}
      </div>

      <Dialog open={!!viewingPolicy} onOpenChange={() => setViewingPolicy(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] bg-[#0F0B1E] border-white/10 p-0" data-testid="policy-popup">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-white/10">
            <DialogTitle className="text-lg font-bold">{viewingPolicy?.title}</DialogTitle>
          </DialogHeader>
          <div className="px-6 py-4 overflow-y-auto" style={{ maxHeight: '60vh' }}>
            {viewingPolicy?.content ? (
              <div className="prose prose-invert prose-sm max-w-none text-gray-300"
                dangerouslySetInnerHTML={{ __html: viewingPolicy.content }} />
            ) : (
              <p className="text-gray-500 text-sm">No content available yet.</p>
            )}
          </div>
          <div className="px-6 py-3 border-t border-white/10 flex justify-end">
            <Button onClick={() => setViewingPolicy(null)} size="sm" data-testid="close-policy-popup">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PolicyConsentCheckbox;
